import { useEffect, useState, useRef, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate, Link } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { ConfirmProvider } from "@/components/ConfirmDialog";

// Pages
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import CampaignView from "@/pages/CampaignView";
import ChaptersPage from "@/pages/ChaptersPage";
import StoryNotebookPage from "@/pages/StoryNotebookPage";
import CharacterSheetPage from "@/pages/CharacterSheetPage";
import CharactersPage from "@/pages/CharactersPage";
import CreaturesPage from "@/pages/CreaturesPage";
import LorePage from "@/pages/LorePage";
import DeitiesPage from "@/pages/DeitiesPage";
import FactionsPage from "@/pages/FactionsPage";
import QuestsPage from "@/pages/QuestsPage";
import MapsPage from "@/pages/MapsPage";
import LocationsPage from "@/pages/LocationsPage";
import LocationDetailPage from "@/pages/LocationDetailPage";
import NpcDetailPage from "@/pages/NpcDetailPage";
import HandoutsPage from "@/pages/HandoutsPage";
import RelationshipsPage from "@/pages/RelationshipsPage";
import EntityDetailPage from "@/pages/EntityDetailPage";
import NotesPage from "@/pages/NotesPage";
import PartyPage from "@/pages/PartyPage";
import JoinCampaign from "@/pages/JoinCampaign";
import DMScreenPage from "@/pages/DMScreenPage";
import CombatTrackerPage from "@/pages/CombatTrackerPage";
import EncounterBuilderPage from "@/pages/EncounterBuilderPage";
import AIOraclePage from "@/pages/AIOraclePage";
import SessionLogPage from "@/pages/SessionLogPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";
import AdminPage from "@/pages/AdminPage";
import SupportPage from "@/pages/SupportPage";
import HelpPage from "@/pages/HelpPage";
import MapDrawerPage from "@/pages/MapDrawerPage";
import LoginPage from "@/pages/LoginPage";
import NPCsPage from "@/pages/NPCsPage";
import ItemsPage from "@/pages/ItemsPage";
import WorldHistoryPage from "@/pages/WorldHistoryPage";
import VTTPage from "@/pages/VTTPage";
import PlayerPortal from "@/pages/PlayerPortal";
import PlayerVTTPage from "@/pages/PlayerVTTPage";
import PlayerWikiPage from "@/pages/PlayerWikiPage";
import TablesPage from "@/pages/TablesPage";
import TableDetailPage from "@/pages/TableDetailPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = (redirectTo = '/dashboard') => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + redirectTo;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser, checkAuth } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          // Exchange session_id for session_token (also sets cookie)
          const sessionRes = await axios.post(`${API}/auth/session`, {
            session_id: sessionId
          }, { withCredentials: true });
          
          // Use the token directly from the response — don't wait for cookie propagation
          const token = sessionRes.data?.session_token;
          
          let userData = null;
          if (token) {
            const res = await axios.get(`${API}/auth/me`, {
              withCredentials: true,
              headers: { Authorization: `Bearer ${token}` }
            });
            userData = res.data;
          } else {
            // Fallback: wait briefly for cookie, then try
            await new Promise(r => setTimeout(r, 500));
            const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
            userData = res.data;
          }
          
          if (userData) {
            setUser(userData);
          }
          
          // Clean the hash fragment from the URL
          const targetPath = window.location.pathname === '/' ? '/dashboard' : window.location.pathname;
          setTimeout(() => navigate(targetPath, { replace: true }), 100);
        } catch (error) {
          console.error("Auth callback error:", error);
          navigate('/login', { replace: true, state: { error: 'Google sign-in failed. Please try again or use email login.' } });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [navigate, setUser, checkAuth]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // If user data passed from AuthCallback, use it
  if (location.state?.user) {
    return children;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId" element={
        <ProtectedRoute>
          <CampaignView />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/chapters" element={
        <ProtectedRoute>
          <StoryNotebookPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/chapters-legacy" element={
        <ProtectedRoute>
          <ChaptersPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/characters" element={
        <ProtectedRoute>
          <CharactersPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/characters/:charId/sheet" element={
        <ProtectedRoute>
          <CharacterSheetPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/creatures" element={
        <ProtectedRoute>
          <CreaturesPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/lore" element={
        <ProtectedRoute>
          <LorePage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/deities" element={
        <ProtectedRoute>
          <DeitiesPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/factions" element={
        <ProtectedRoute>
          <FactionsPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/quests" element={
        <ProtectedRoute>
          <QuestsPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/tables" element={
        <ProtectedRoute>
          <TablesPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/tables/:tableId" element={
        <ProtectedRoute>
          <TableDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/maps" element={
        <ProtectedRoute>
          <MapsPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/world-atlas" element={
        <ProtectedRoute>
          <Navigate to="../maps" relative="path" replace />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/locations" element={
        <ProtectedRoute>
          <LocationsPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/locations/:locId" element={
        <ProtectedRoute>
          <LocationDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/handouts" element={
        <ProtectedRoute>
          <HandoutsPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/relationships" element={
        <ProtectedRoute>
          <RelationshipsPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/npcs" element={
        <ProtectedRoute>
          <NPCsPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/npcs/:npcId" element={
        <ProtectedRoute>
          <NpcDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/items" element={
        <ProtectedRoute>
          <ItemsPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/world-history" element={
        <ProtectedRoute>
          <WorldHistoryPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/:entityType/:entityId" element={
        <ProtectedRoute>
          <EntityDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/notes" element={
        <ProtectedRoute>
          <NotesPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/party" element={
        <ProtectedRoute>
          <PartyPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/dm-screen" element={
        <ProtectedRoute>
          <DMScreenPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/combat" element={
        <ProtectedRoute>
          <CombatTrackerPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/encounter-builder" element={
        <ProtectedRoute>
          <EncounterBuilderPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/oracle" element={
        <ProtectedRoute>
          <AIOraclePage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/sessions" element={
        <ProtectedRoute>
          <Navigate to="../chapters" replace />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/sessions-legacy" element={
        <ProtectedRoute>
          <SessionLogPage />
        </ProtectedRoute>
      } />
      <Route path="/join/:inviteCode" element={<JoinCampaign />} />
      <Route path="/campaign/:campaignId/vtt" element={
        <ProtectedRoute>
          <VTTPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/player-portal" element={<PlayerPortal />} />
      <Route path="/campaign/:campaignId/wiki" element={<PlayerWikiPage />} />
      <Route path="/campaign/:campaignId/player-vtt" element={<PlayerVTTPage />} />
      <Route path="/subscription" element={
        <ProtectedRoute>
          <SubscriptionPage />
        </ProtectedRoute>
      } />
      <Route path="/subscription/success" element={
        <ProtectedRoute>
          <SubscriptionSuccess />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPage />
        </ProtectedRoute>
      } />
      <Route path="/support" element={
        <ProtectedRoute>
          <SupportPage />
        </ProtectedRoute>
      } />
      <Route path="/help" element={
        <ProtectedRoute>
          <HelpPage />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:campaignId/maps/draw" element={
        <ProtectedRoute>
          <MapDrawerPage />
        </ProtectedRoute>
      } />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ConfirmProvider>
            <AppRouter />
          </ConfirmProvider>
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
